/**
 * Chart export utilities — SVG-to-PNG, HTML-to-PNG, clipboard copy, blob download.
 * No external dependencies; uses native browser APIs.
 */

const EXPORT_SCALE = 2; // 2x for retina-quality PNGs
const EXPORT_BG = '#0a0a0a'; // match dark theme background

/**
 * Find the primary renderable element inside a container.
 * Prefers <svg> (Recharts), falls back to the container itself (HTML tables).
 */
function findRenderable(container: HTMLElement): SVGSVGElement | HTMLElement {
  // Find the largest SVG (the chart), not tiny icon SVGs in buttons
  const svgs = container.querySelectorAll('svg');
  let best: SVGSVGElement | null = null;
  let bestArea = 0;
  for (const svg of svgs) {
    const rect = svg.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (area > bestArea) {
      bestArea = area;
      best = svg;
    }
  }
  return best || container;
}

/**
 * Convert an SVG element to a PNG Blob via canvas.
 */
async function svgToPngBlob(svg: SVGSVGElement): Promise<Blob> {
  const clone = svg.cloneNode(true) as SVGSVGElement;

  // Ensure dimensions are set
  const rect = svg.getBoundingClientRect();
  const width = rect.width;
  const height = rect.height;
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  // Inline computed styles for correct rendering
  inlineStyles(svg, clone);

  const svgData = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width * EXPORT_SCALE;
    canvas.height = height * EXPORT_SCALE;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = EXPORT_BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(EXPORT_SCALE, EXPORT_SCALE);
    ctx.drawImage(img, 0, 0, width, height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Convert an HTML element to a PNG Blob via canvas.
 * Uses a foreignObject approach for HTML tables (heatmap, etc.).
 */
async function htmlToPngBlob(element: HTMLElement): Promise<Blob> {
  const rect = element.getBoundingClientRect();
  const width = rect.width;
  const height = rect.height;

  // Clone and inline styles
  const clone = element.cloneNode(true) as HTMLElement;
  inlineStylesHTML(element, clone);

  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('xmlns', svgNs);

  const fo = document.createElementNS(svgNs, 'foreignObject');
  fo.setAttribute('width', '100%');
  fo.setAttribute('height', '100%');

  const body = document.createElement('body');
  body.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  body.style.margin = '0';
  body.style.padding = '0';
  body.style.backgroundColor = EXPORT_BG;
  body.appendChild(clone);
  fo.appendChild(body);
  svg.appendChild(fo);

  const svgData = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width * EXPORT_SCALE;
    canvas.height = height * EXPORT_SCALE;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = EXPORT_BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(EXPORT_SCALE, EXPORT_SCALE);
    ctx.drawImage(img, 0, 0, width, height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Convert any chart container to a PNG blob.
 * Auto-detects SVG vs HTML content.
 */
export async function chartToPngBlob(container: HTMLElement): Promise<Blob> {
  const renderable = findRenderable(container);
  if (renderable instanceof SVGSVGElement) {
    return svgToPngBlob(renderable);
  }
  return htmlToPngBlob(renderable);
}

/**
 * Copy chart as PNG to clipboard.
 */
export async function copyChartToClipboard(container: HTMLElement): Promise<void> {
  const blob = await chartToPngBlob(container);
  await navigator.clipboard.write([
    new ClipboardItem({ 'image/png': blob }),
  ]);
}

/**
 * Download a Blob as a file.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download chart container as PNG file.
 */
export async function downloadChartAsPng(container: HTMLElement, filename: string): Promise<void> {
  const blob = await chartToPngBlob(container);
  downloadBlob(blob, filename.endsWith('.png') ? filename : `${filename}.png`);
}

// ── Helpers ──────────────────────────────────────────────────────────────

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Copy computed styles from source SVG elements to cloned SVG elements.
 * This ensures text rendering, colors, and fonts survive serialization.
 */
function inlineStyles(source: Element, target: Element): void {
  const computed = window.getComputedStyle(source);
  const important = ['fill', 'stroke', 'stroke-width', 'font-family', 'font-size',
    'font-weight', 'text-anchor', 'dominant-baseline', 'color', 'opacity'];
  for (const prop of important) {
    const val = computed.getPropertyValue(prop);
    if (val && val !== 'none' && val !== 'normal' && val !== '0') {
      (target as SVGElement).style.setProperty(prop, val);
    }
  }
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let i = 0; i < sourceChildren.length && i < targetChildren.length; i++) {
    inlineStyles(sourceChildren[i], targetChildren[i]);
  }
}

/**
 * Copy computed styles from source HTML elements to cloned HTML elements.
 */
function inlineStylesHTML(source: Element, target: Element): void {
  if (source instanceof HTMLElement && target instanceof HTMLElement) {
    const computed = window.getComputedStyle(source);
    const important = ['color', 'background-color', 'font-family', 'font-size',
      'font-weight', 'padding', 'margin', 'border', 'text-align', 'white-space',
      'display', 'flex-direction', 'gap', 'width', 'min-width', 'max-width'];
    for (const prop of important) {
      target.style.setProperty(prop, computed.getPropertyValue(prop));
    }
  }
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let i = 0; i < sourceChildren.length && i < targetChildren.length; i++) {
    inlineStylesHTML(sourceChildren[i], targetChildren[i]);
  }
}
